import { useCallback, createContext, useContext } from "react";
import { injectIntl } from "react-intl";

import { formatDate, formatTime } from "../../helpers/DateTimeHelper";

export const IntlContext = createContext();

// turn the old context into the new context
export const InjectIntlContext = injectIntl(({ intl, children }) => {
  return <IntlContext.Provider value={intl}>{children}</IntlContext.Provider>;
});

// the format message hook
export const useFormatMessage = () => {
  const intl = useIntl();
  // The safe navigation operator allows components that are rendered outside of the
  // global context (like when rendered with ReactDOMServer.renderToString) to render the
  // default message as we can't fetch the intl context.
  return intl?.formatMessage || (({ defaultMessage }) => defaultMessage);
};

// This hook allows to use custom messages and fallback to the default messages.
//
// Note that for now custom messages do not support variable interpolation.
export const useFormatMessageWithCustomization = customIntlMessages => {
  const formatMessage = useFormatMessage();

  return useCallback(
    (message, values) => {
      if (!customIntlMessages) return formatMessage(message, values);

      const customMessage = customIntlMessages.find(
        customMessage => customMessage.identifier === message.id
      );

      if (customMessage) {
        return customMessage.message;
      } else {
        return formatMessage(message, values);
      }
    },
    [customIntlMessages, formatMessage]
  );
};

// the format message hook
export const useLocale = () => {
  const intl = useIntl();
  return intl.locale;
};

// Allows formatting a date without having to explicitly pass the `intl` argument.
export const useFormatDate = () => {
  const intl = useIntl();
  return (...args) => formatDate(intl, ...args);
};

// Allows formatting a datetime without having to explicitly pass the `intl` argument.
export const useFormatTime = () => {
  const intl = useIntl();
  return (...args) => formatTime(intl, ...args);
};

// the format message hook
export const useIntl = () => {
  return useContext(IntlContext);
};
